// CrazyGames HTML5 SDK integration for Warbirds.io.
//
// CrazyGames frames the game from its own origin (games.crazygames.com, files
// from <slug>.game-files.crazygames.com) exactly like the itch.io embed does —
// the front-end is a static upload that talks back to the real Warbirds server
// cross-origin (the server grants CORS to *.crazygames.com and the WS check is
// permissive; see internal/server/cors.go and server.go). The one thing the
// portal adds on top of the itch build is this SDK: CrazyGames requires the
// game to announce its lifecycle so the platform can time loads, count
// sessions, and (later, at full launch) place ads between rounds.
//
// The SDK is a classic <script> injected into the page <head> by the
// build-crazygames packager (client/scripts/build-crazygames.mjs) — it defines
// window.CrazyGames before this module (bundled into game.js) runs. On every
// other origin (warbirds.io itself, the itch build) that script is absent, so
// window.CrazyGames is undefined and every method here degrades to a silent
// no-op. That's the whole gate: no theater flag, no build flag — the presence
// of the SDK object is the switch, and with it off the game runs byte-for-byte
// as before. Matches the analytics.js "degrade to no-op when the loader didn't
// run" pattern.
//
// A BASIC launch needs only three of these hooks wired: init(), the loading
// bracket (init → ready), and the gameplay bracket (setGameplayActive). Ads are
// intentionally NOT called for a basic launch — the platform won't serve them
// yet, and the mute/pause plumbing is a full-launch concern. midgameAd() and
// rewardedAd() are implemented and ready so the full-launch flip is a one-line
// call from the death flow, but nothing invokes them today.

// The SDK object, or null when the loader script isn't on the page (the normal
// case everywhere except a CrazyGames-hosted build). Captured once; the loader
// is synchronous and runs in <head> before this module, so if it's coming it's
// already here.
function sdk() {
  return (typeof window !== "undefined" && window.CrazyGames && window.CrazyGames.SDK) || null;
}

let initialized = false; // SDK.init() resolved — game.* calls are safe
let loadingOpen = false; // loadingStart fired, awaiting the matching loadingStop
let gameplayActive = false; // last state pushed to gameplayStart/gameplayStop

// init awaits the SDK's async initialization and opens the loading bracket.
// Call it as early as boot() can — before the config fetch and asset build — so
// the loading timer brackets the whole cold start. Safe to await on any origin:
// with no SDK it resolves immediately and the game boots exactly as before.
// Never throws; an SDK that errors must not take the game down with it.
export async function init() {
  const s = sdk();
  if (!s) return;
  try {
    await s.init();
    initialized = true;
    // loadingStart/Stop measure the cold-start the player actually waits
    // through; pair it with ready() at first frame.
    s.game.loadingStart();
    loadingOpen = true;
  } catch (err) {
    // A dead SDK just means no platform telemetry — the game is still fully
    // playable, so swallow it and carry on.
    console.warn("CrazyGames SDK init failed:", err);
  }
}

// ready closes the loading bracket. Call it once the game is interactive — the
// join screen is up and the render loop is running — which is CrazyGames' "the
// player can start" moment for the load-time metric.
export function ready() {
  const s = sdk();
  if (!s || !initialized || !loadingOpen) return;
  try {
    s.game.loadingStop();
  } catch (err) {
    console.warn("CrazyGames SDK loadingStop failed:", err);
  }
  loadingOpen = false;
}

// setGameplayActive drives the gameplay bracket from a single derived boolean —
// true while the player is alive and in the world, false on the join/respawn
// screen, while spectating, or disconnected. Edge-triggered: it only fires
// gameplayStart/gameplayStop on a real transition, so it's cheap to call every
// frame (that's the intended use — poll the same "in gameplay" state the render
// loop already computes). CrazyGames treats every menu/death/pause as a break,
// which is exactly the alive→dead transition here.
export function setGameplayActive(active) {
  active = !!active;
  if (active === gameplayActive) return;
  gameplayActive = active;
  const s = sdk();
  if (!s || !initialized) return;
  try {
    if (active) s.game.gameplayStart();
    else s.game.gameplayStop();
  } catch (err) {
    console.warn("CrazyGames SDK gameplay event failed:", err);
  }
}

// requestAd runs a video ad and brackets it with mute/unmute so the game goes
// quiet under the ad's own audio and comes back after. `onMuted(bool)` is the
// game's hook to silence and pause (true) then restore (false); it's called for
// both the success and the error/unfilled paths, because ad calls are
// frequently unfilled (adblock, timing caps, low demand) and the game must
// always come back. `onReward` fires only on a fully-watched rewarded ad.
// Returns a promise that resolves true if a reward was earned. NOT wired for the
// basic launch — see the module header — but ready for the full-launch flip:
//   requestAd("midgame", { onMuted: (m) => audio.setMuted(m) })   // on death
//   requestAd("rewarded", { onMuted, onReward: grantRespawnPerk })
function requestAd(type, { onMuted, onReward } = {}) {
  return new Promise((resolve) => {
    const s = sdk();
    if (!s || !initialized) {
      resolve(false);
      return;
    }
    let rewarded = false;
    const done = () => {
      onMuted && onMuted(false);
      resolve(rewarded);
    };
    try {
      s.ad.requestAd(type, {
        adStarted: () => onMuted && onMuted(true),
        adFinished: () => {
          if (type === "rewarded") {
            rewarded = true;
            onReward && onReward();
          }
          done();
        },
        adError: () => done(), // unfilled or blocked: unmute and move on
      });
    } catch (err) {
      console.warn("CrazyGames SDK requestAd failed:", err);
      done();
    }
  });
}

export function midgameAd(opts) {
  return requestAd("midgame", opts);
}

export function rewardedAd(opts) {
  return requestAd("rewarded", opts);
}

// --- Multiplayer: identity + the room system ----------------------------------
//
// CrazyGames' Join/Invite UI, friend list, and multiplayer landing page are all
// driven by two things the game must feed the SDK: the player's account
// username (so friends recognize each other in-game) and the "room" the player
// occupies (so the platform knows whose game a friend can drop into). Warbirds
// isn't lobby-based — each theater is one persistent public world that holds
// 20+ players, which CrazyGames explicitly allows ("place the player directly
// into public gameplay"). So the "room" here is that shared world: the player
// joins it on takeoff and it's always joinable; there's no private lobby to
// create. These are the multiplayer-specific launch requirements; all no-op
// off-portal exactly like the rest of the module.

// getUsername resolves the player's CrazyGames account username, or null when
// there's no SDK, no signed-in account, or the lookup fails. Used to prefill the
// callsign so a friend spots you on the scoreboard.
export async function getUsername() {
  const s = sdk();
  if (!s || !initialized) return null;
  try {
    const user = await s.user.getUser();
    return (user && user.username) || null;
  } catch {
    // No account signed in (or the module rejected): the player just types a
    // callsign like always.
    return null;
  }
}

// updateRoom tells the platform which room the player is in and whether others
// may join, powering the CrazyGames Join/Invite UI. For our shared-world model
// the room is the live world and it's always joinable; inviteParams ride along
// to a friend who accepts the invite (e.g. the theater, so they land in the
// same fight). Call it on takeoff.
export function updateRoom(params) {
  const s = sdk();
  if (!s || !initialized) return;
  try {
    s.game.updateRoom(params);
  } catch (err) {
    console.warn("CrazyGames SDK updateRoom failed:", err);
  }
}

// leftRoom clears room membership (on disconnect / leaving the world) so the
// platform stops advertising the seat as joinable.
export function leftRoom() {
  const s = sdk();
  if (!s || !initialized) return;
  try {
    s.game.leftRoom();
  } catch (err) {
    console.warn("CrazyGames SDK leftRoom failed:", err);
  }
}

// isInstantMultiplayer is set when the platform launched the game expecting an
// immediate drop into multiplayer — a friend clicked "play together", or the
// player came from the multiplayer landing page. When true the game should skip
// its menus and start playing with defaults.
export function isInstantMultiplayer() {
  const s = sdk();
  if (!s || !initialized) return false;
  try {
    return !!s.game.isInstantMultiplayer;
  } catch {
    return false;
  }
}

// getInviteParam reads a value the inviter baked into their invite link (the
// same keys passed to updateRoom's inviteParams / inviteLink), or null. A
// non-null value means this player arrived via a friend's invite and should be
// dropped straight into the shared world.
export function getInviteParam(key) {
  const s = sdk();
  if (!s || !initialized) return null;
  try {
    return s.game.getInviteParam(key) ?? null;
  } catch {
    return null;
  }
}

// addJoinRoomListener registers a callback the platform invokes when a friend
// asks to join mid-session (without a page reload). For a shared world that
// just means "make sure this player is in the game."
export function addJoinRoomListener(fn) {
  const s = sdk();
  if (!s || !initialized) return;
  try {
    s.game.addJoinRoomListener(fn);
  } catch (err) {
    console.warn("CrazyGames SDK addJoinRoomListener failed:", err);
  }
}

// inviteLink builds a shareable CrazyGames URL carrying the given params, for an
// in-game "invite a friend" button. Returns "" when unavailable.
export function inviteLink(params) {
  const s = sdk();
  if (!s || !initialized) return "";
  try {
    return s.game.inviteLink(params) || "";
  } catch {
    return "";
  }
}

// available reports whether we're running inside a live CrazyGames build (the
// SDK loader is present and initialized). Used to reveal portal-only UI, like
// the in-game invite button, that would be dead weight anywhere else.
export function available() {
  return !!sdk() && initialized;
}

// chatDisabled reflects the platform's request to turn off in-game chat — set
// for moderated contexts (e.g. a minor's session). The game must honor it, both
// for the send composer and for showing other players' messages (a multiplayer
// compliance requirement). Read live so a mid-session flip (delivered via
// onSettingsChange) takes effect immediately; false off-portal, so chat runs
// exactly as before everywhere else.
export function chatDisabled() {
  const s = sdk();
  if (!s || !initialized) return false;
  try {
    return !!(s.game.settings && s.game.settings.disableChat);
  } catch {
    return false;
  }
}

// onSettingsChange registers a callback the SDK fires whenever platform settings
// change — the disableChat flag flipping mid-session is the one that matters
// here — so the game can react live (close an open composer, re-hide the log).
export function onSettingsChange(fn) {
  const s = sdk();
  if (!s || !initialized) return;
  try {
    s.game.addSettingsChangeListener(fn);
  } catch (err) {
    console.warn("CrazyGames SDK addSettingsChangeListener failed:", err);
  }
}

// Single object export mirroring analytics.js, so callers read
// `crazyGames.setGameplayActive(...)` at the call site.
export const crazyGames = {
  init,
  ready,
  setGameplayActive,
  midgameAd,
  rewardedAd,
  getUsername,
  updateRoom,
  leftRoom,
  isInstantMultiplayer,
  getInviteParam,
  addJoinRoomListener,
  inviteLink,
  available,
  chatDisabled,
  onSettingsChange,
};
